import { FC, memo, useEffect } from 'react';
import { NumberFormatValues, NumericFormat } from 'react-number-format';
import { TextField, Typography } from '@mui/material';
import classNames from 'classnames';
import { showToast, useAppDispatch } from 'store';
import { Checkbox } from 'components/shared/Checkbox';
import { CURRENCY_LABEL } from 'utils/enums';
import { convertCoinsToUAH, getErrorMessage, handleKeyDownServiceAmount } from 'utils/helpers';
import { useDebounce } from 'utils/hooks';
import { BillService } from 'utils/services';
import { INormalizedServiceBillRequestItem, ServiceFeeEntry } from 'utils/types';
import styles from './index.module.scss';

interface ServiceBillRequestLineProps {
	service: INormalizedServiceBillRequestItem;
	isSelected: boolean;
	onSelect: (service: INormalizedServiceBillRequestItem) => void;
	onChangeAmount: (service: INormalizedServiceBillRequestItem, amount: string) => void;
	onChangeFee: (feeEntry: ServiceFeeEntry) => void;
}

export const ServiceBillRequestLine: FC<ServiceBillRequestLineProps> = memo(
	({ service, isSelected, onSelect, onChangeAmount, onChangeFee }) => {
		const dispatch = useAppDispatch();
		const amount = service.incomeBalance === null ? '' : convertCoinsToUAH(service.incomeBalance).toString();
		const amountInCoins = service.incomeBalance === null ? null : Number(service.incomeBalance);
		const debouncedAmount = useDebounce<number | null>(amountInCoins, 700);
		const isZeroAmount = service.incomeBalance === '0';
		const isAmountDisabled = service.valuesDisabled || isZeroAmount;
		const isSelectionDisabled = Number(service.incomeBalance) <= 0;

		useEffect(() => {
			let isActive = true;

			const getServiceFee = async (): Promise<void> => {
				if (!debouncedAmount) {
					onChangeFee({ fee: 0, feeHostCard: 0, serviceId: service.selectionKey });
					return;
				}

				try {
					const { data } = await BillService.getServiceFee(debouncedAmount, service.code, service.providerType, true);
					if (isActive) {
						onChangeFee({ ...data, serviceId: service.selectionKey });
					}
				} catch (error) {
					if (isActive) {
						dispatch(showToast({ message: getErrorMessage(error) }));
					}
				}
			};

			getServiceFee();

			return () => {
				isActive = false;
			};
		}, [debouncedAmount, dispatch, onChangeFee, service.code, service.providerType, service.selectionKey]);

		const handleChangeAmount = ({ value }: NumberFormatValues): void => {
			onChangeAmount(service, value);
		};

		return (
			<div
				className={classNames(styles.item, {
					[styles.item_selected]: isSelected,
					[styles.item_requested]: service.requestedService,
					[styles.item_disabled]: isZeroAmount,
				})}
			>
				<div className={styles.info}>
					<div className={styles.titleRow}>
						<Typography className={styles.title} variant="subtitle2">
							{service.displayName}
						</Typography>
						{service.requestedService && (
							<Typography className={styles.requested} variant="caption">
								Запитана послуга
							</Typography>
						)}
					</div>
					{!!service.providerAccountId && (
						<Typography className={styles.account} variant="caption">
							Особовий рахунок: {service.providerAccountId}
						</Typography>
					)}
					{isZeroAmount && (
						<Typography className={styles.status} variant="caption">
							Немає суми до сплати
						</Typography>
					)}
					{service.valuesDisabled && !isZeroAmount && (
						<Typography className={styles.status} variant="caption">
							Суму визначено постачальником
						</Typography>
					)}
				</div>
				<div className={styles.actions}>
					<NumericFormat
						aria-label={`Сума до сплати за послугу ${service.displayName}`}
						className={styles.amount}
						customInput={TextField}
						decimalScale={2}
						value={amount}
						onValueChange={handleChangeAmount}
						allowNegative={false}
						allowLeadingZeros
						fixedDecimalScale
						thousandSeparator=" "
						suffix={CURRENCY_LABEL.UAH}
						size="small"
						placeholder="0,00 грн"
						inputProps={{ inputMode: 'decimal', autoComplete: 'off' }}
						disabled={isAmountDisabled}
						allowedDecimalSeparators={[',']}
						onKeyDown={(event) => handleKeyDownServiceAmount(event, amount)}
					/>
					<Checkbox
						aria-label={`Обрати послугу ${service.displayName}`}
						checked={isSelected}
						disabled={isSelectionDisabled}
						onChange={() => onSelect(service)}
					/>
				</div>
			</div>
		);
	}
);
